import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllReports, resetAdminReports, deleteReport } from '../../../store/slices/gameTestingSlice';
import ReportDetailView from '../../admin/AdminGameTesting/components/ReportDetailView';
import './AnalyticsReportsList.scss';

const AnalyticsReportsList = () => {
  const dispatch = useDispatch();
  const { 
    adminReports: reports, 
    adminReportsLoading: loading, 
    adminReportsError: error,
    adminHasMore: hasMore,
    adminLastVisible: lastVisible
  } = useSelector(state => state.gameTesting);
  
  const [selectedReport, setSelectedReport] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Stats calculation
  const [stats, setStats] = useState({
    totalReports: 0,
    pendingReports: 0,
    completedReports: 0,
    developmentReports: 0,
    uniqueAssignees: 0
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    assignedTo: '',
    weekRange: '',
    dateRange: {
      start: '',
      end: ''
    },
    searchTerm: ''
  });

  useEffect(() => {
    // Reset and fetch initial reports
    dispatch(resetAdminReports());
    dispatch(fetchAllReports({ 
      itemsPerPage: 10,
      lastVisible: null,
      filters: {}
    }));
  }, [dispatch]);
  
  // Calculate stats when reports change
  useEffect(() => {
    if (reports && reports.length > 0) {
      const pendingReports = reports.filter(report => report.status === 'pending_testing').length;
      const completedReports = reports.filter(report => report.status === 'tested_and_resolved').length;
      const developmentReports = reports.filter(report => report.status === 'assigned_to_developer').length;
      
      // Get unique assignees
      const uniqueAssignees = new Set(reports.map(report => report.assignedTo?.email).filter(Boolean)).size;
      
      setStats({
        totalReports: reports.length,
        pendingReports,
        completedReports,
        developmentReports,
        uniqueAssignees
      });
    }
  }, [reports]);

  const loadMoreReports = () => {
    if (!hasMore || loading) return;
    
    setLoadingMore(true);
    dispatch(fetchAllReports({ 
      itemsPerPage: 10,
      lastVisible,
      filters
    })).finally(() => {
      setLoadingMore(false);
    });
  };

  const applyFilters = () => {
    // Reset and fetch with filters
    dispatch(resetAdminReports());
    dispatch(fetchAllReports({ 
      itemsPerPage: 10,
      lastVisible: null,
      filters: {
        status: filters.status,
        assignedTo: filters.assignedTo,
        weekRange: filters.weekRange
      }
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      assignedTo: '',
      weekRange: '',
      dateRange: {
        start: '',
        end: ''
      },
      searchTerm: ''
    });
    
    // Reset the reports list and fetch without filters
    dispatch(resetAdminReports());
    dispatch(fetchAllReports({ 
      itemsPerPage: 10,
      lastVisible: null,
      filters: {}
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_testing: { color: 'warning', text: 'Pending Testing' },
      test_report_available: { color: 'info', text: 'Test Report Available' },
      assigned_to_developer: { color: 'primary', text: 'Assigned to Developer' },
      tested_and_resolved: { color: 'success', text: 'Tested and Resolved' }
    };

    const config = statusConfig[status] || { color: 'secondary', text: status };
    return <span className={`status-badge ${config.color}`}>{config.text}</span>;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      let date;
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        // Firebase Timestamp object - convert seconds to milliseconds
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'number') {
        // Handle both seconds and milliseconds
        date = new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp);
      } else {
        date = new Date(timestamp);
      }
      
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Invalid Date';
    }
  };

  const handleDeleteReport = async (reportId) => {
    try {
      await dispatch(deleteReport(reportId)).unwrap();
      // Close modal - Redux state is automatically updated by deleteReport action
      setShowViewModal(false);
      setSelectedReport(null);
    } catch (error) {
      console.error('Error deleting report:', error);
      // Could show an error message to user
    }
  };

  // Filter and sort reports by search term and lastUpdated
  const filteredReports = reports
    .filter(report => {
      if (!filters.searchTerm) return true;
      
      const searchTerm = filters.searchTerm.toLowerCase();
      return (
        (report.assignedTo?.name?.toLowerCase()?.includes(searchTerm)) ||
        (report.assignedTo?.email?.toLowerCase()?.includes(searchTerm)) ||
        (report.weekRange?.toLowerCase()?.includes(searchTerm))
      );
    })
    .sort((a, b) => {
      // Sort by lastUpdated in descending order (newest first)
      const convertToDate = (timestamp) => {
        if (!timestamp) return new Date(0);
        if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
          return new Date(timestamp.seconds * 1000);
        } else if (timestamp instanceof Date) {
          return timestamp;
        } else if (typeof timestamp === 'number') {
          return new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp);
        } else {
          return new Date(timestamp);
        }
      };
      
      const dateA = convertToDate(a.lastUpdated);
      const dateB = convertToDate(b.lastUpdated);
      return dateB.getTime() - dateA.getTime();
    });

  // Get unique week ranges for filter
  const weekRanges = [...new Set(reports.map(report => report.weekRange))].sort().reverse();

  // Get unique assignees for filter
  const assignees = [...new Set(reports.map(report => report.assignedTo?.email))].filter(Boolean);

  if (loading && !loadingMore && reports.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading analytics data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error loading data: {error}</p>
        <button onClick={() => dispatch(fetchAllReports({ itemsPerPage: 10 }))}>Retry</button>
      </div>
    );
  }

  return (
    <div className="analytics-reports-container">
      <div className="statistics-summary">
        <div className="stat-card">
          <h3>Total Reports</h3>
          <div className="stat-value">{stats.totalReports}</div>
        </div>
        <div className="stat-card">
          <h3>Pending Reports</h3>
          <div className="stat-value">{stats.pendingReports}</div>
        </div>
        <div className="stat-card">
          <h3>In Development</h3>
          <div className="stat-value">{stats.developmentReports}</div>
        </div>
        <div className="stat-card">
          <h3>Completed</h3>
          <div className="stat-value">{stats.completedReports}</div>
        </div>
        <div className="stat-card">
          <h3>Unique Testers</h3>
          <div className="stat-value">{stats.uniqueAssignees}</div>
        </div>
      </div>
      
      <div className="filters-section">
        <h2>Analytics Reports</h2>
        
        <div className="filters-row">
          <div className="filter-item">
            <label>Status:</label>
            <select 
              value={filters.status} 
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="pending_testing">Pending Testing</option>
              <option value="test_report_available">Test Report Available</option>
              <option value="assigned_to_developer">Assigned to Developer</option>
              <option value="tested_and_resolved">Tested and Resolved</option>
            </select>
          </div>
          
          <div className="filter-item">
            <label>Assigned To:</label>
            <select 
              value={filters.assignedTo} 
              onChange={(e) => handleFilterChange('assignedTo', e.target.value)}
            >
              <option value="">All Users</option>
              {assignees.map(email => (
                <option key={email} value={email}>
                  {email}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-item">
            <label>Week Range:</label>
            <select 
              value={filters.weekRange} 
              onChange={(e) => handleFilterChange('weekRange', e.target.value)}
            >
              <option value="">All Weeks</option>
              {weekRanges.map(week => (
                <option key={week} value={week}>
                  {week}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-item">
            <label>Search:</label>
            <input 
              type="text" 
              value={filters.searchTerm} 
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              placeholder="Search by name or email"
            />
          </div>
          
          <button 
            className="filter-button" 
            onClick={applyFilters}
            disabled={loading}
          >
            Apply Filters
          </button>
          
          <button 
            className="reset-button" 
            onClick={resetFilters}
            disabled={loading}
          >
            Reset
          </button>
        </div>
      </div>
      
      <div className="reports-table">
        <table>
          <thead>
            <tr>
              <th>Assignee</th>
              <th>Status</th>
              <th>Week Range</th>
              <th>Games</th>
              <th>Submitted</th>
              <th>Last Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReports.length > 0 ? (
              filteredReports.map(report => (
                <tr key={report.id}>
                  <td>
                    <div className="assignee-info">
                      <span className="assignee-name">{report.assignedTo?.name || 'N/A'}</span>
                      <span className="assignee-email">{report.assignedTo?.email || 'N/A'}</span>
                    </div>
                  </td>
                  <td>{getStatusBadge(report.status)}</td>
                  <td>{report.weekRange || 'N/A'}</td>
                  <td>
                    <div className="games-list">
                      {report.selectedGames?.length > 0 
                        ? report.selectedGames.join(', ') 
                        : 'None selected'}
                    </div>
                  </td>
                  <td>{formatDate(report.createdAt)}</td>
                  <td>{formatDate(report.lastUpdated)}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="view-button"
                        onClick={() => {
                          setSelectedReport(report);
                          setShowViewModal(true);
                        }}
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-reports">
                  No reports found. Adjust filters or add new reports.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        {hasMore && reports.length > 0 && (
          <div className="load-more">
            <button 
              className="load-more-button"
              onClick={loadMoreReports}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <>
                  <span className="spinner"></span>
                  Loading more...
                </>
              ) : (
                'Load More'
              )}
            </button>
          </div>
        )}
        
        {loadingMore && (
          <div className="loading-more">
            <div className="loading-spinner-small"></div>
            <span>Loading more reports...</span>
          </div>
        )}
      </div>
      
      {showViewModal && (
        <ReportDetailView 
          report={selectedReport}
          onClose={() => {
            setShowViewModal(false);
            setSelectedReport(null);
          }}
          onStatusUpdate={() => {
            setShowViewModal(false);
            setSelectedReport(null);
          }}
          onDelete={handleDeleteReport}
        />
      )}
    </div>
  );
};

export default AnalyticsReportsList; 